import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Star, Phone, ArrowLeft, Wifi, Car, Utensils, 
  Clock, Users, Bed, Bath, ChevronLeft, ChevronRight,
  Calendar, MessageCircle
} from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import { PROPERTIES, PROPERTY_IMAGES } from '../utils/constants';
import { ROOM_CATEGORIES } from '../utils/cinematicData';

const PropertyDetail = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Find property by slug
  const property = PROPERTIES.find(p => p.id === slug);
  
  // Get property-specific data
  const getPropertyData = () => {
    if (slug === 'jalsa-retreat') {
      return {
        images: [
          PROPERTY_IMAGES.jalsaRetreat.hero,
          PROPERTY_IMAGES.jalsaRetreat.exterior,
          PROPERTY_IMAGES.jalsaRetreat.sarovarGarden,
          PROPERTY_IMAGES.jalsaRetreat.sarovarGarden2,
          PROPERTY_IMAGES.jalsaRetreat.banquet,
          PROPERTY_IMAGES.jalsaRetreat.reception,
          PROPERTY_IMAGES.jalsaRetreat.gym,
          ...PROPERTY_IMAGES.jalsaRetreat.rooms,
          PROPERTY_IMAGES.jalsaRetreat.dining
        ],
        rooms: ROOM_CATEGORIES.jalsaRetreat,
        whatsapp: '+919098051796',
        highlights: ['Sarovar Garden (Floating Venue)', "Bhopal's Largest Banquet", 'Premium Gym', 'Wedding Specialists']
      };
    } else if (slug === 'jalsa-inn') {
      return {
        images: [
          PROPERTY_IMAGES.jalsaInn.hero,
          PROPERTY_IMAGES.jalsaInn.exterior,
          ...PROPERTY_IMAGES.jalsaInn.rooms,
          PROPERTY_IMAGES.jalsaInn.dining
        ],
        rooms: ROOM_CATEGORIES.jalsaInn,
        whatsapp: '+919244941942',
        highlights: ['Excellent Service', 'Family Friendly', 'Quality Dining', 'Comfortable Rooms']
      };
    } else {
      return {
        images: [
          PROPERTY_IMAGES.hotelJalsa.hero,
          PROPERTY_IMAGES.hotelJalsa.exterior,
          PROPERTY_IMAGES.hotelJalsa.lobby,
          ...PROPERTY_IMAGES.hotelJalsa.rooms,
          PROPERTY_IMAGES.hotelJalsa.dining
        ],
        rooms: ROOM_CATEGORIES.hotelJalsa,
        whatsapp: '+918770722817',
        highlights: ['Central Location', 'Business Ready', 'Modern Amenities', 'Easy Connectivity']
      };
    }
  };

  const propertyData = property ? getPropertyData() : null;

  // Auto-slide images every 5 seconds
  useEffect(() => {
    if (!propertyData) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % propertyData.images.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [propertyData]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Auto-scroll to rooms section if ?book=true in URL
  useEffect(() => {
    if (searchParams.get('book') === 'true') {
      setTimeout(() => {
        const roomsSection = document.getElementById('rooms-section');
        if (roomsSection) {
          roomsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [searchParams]);

  if (!property) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-4xl font-serif mb-4">Property Not Found</h1>
          <Link to="/" className="text-gold-400 hover:underline">Return to Homepage</Link>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % propertyData.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + propertyData.images.length) % propertyData.images.length);
  };

  const whatsappLink = `https://wa.me/${propertyData.whatsapp.replace(/\+/g, '')}?text=Hi, I'm interested in booking at ${property.name}. Please share availability.`;

  return (
    <div className="bg-white">
      {/* Hero Section with Gallery */}
      <section className="relative h-[70vh] overflow-hidden">
        {/* Image Gallery */}
        <div className="absolute inset-0">
          {propertyData.images.map((img, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: currentImageIndex === index ? 1 : 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              <img
                src={img}
                alt={`${property.name} - ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/40 to-navy-900/60" />
        </div>

        {/* Gallery Navigation */}
        <button
          onClick={prevImage}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm hover:bg-gold-400 rounded-full flex items-center justify-center transition-all duration-300 group"
        >
          <ChevronLeft className="w-6 h-6 text-white group-hover:text-navy-900" />
        </button>
        <button
          onClick={nextImage}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm hover:bg-gold-400 rounded-full flex items-center justify-center transition-all duration-300 group"
        >
          <ChevronRight className="w-6 h-6 text-white group-hover:text-navy-900" />
        </button>

        {/* Image Indicators */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
          {propertyData.images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                currentImageIndex === index ? 'w-8 bg-gold-400' : 'w-4 bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>

        {/* Back Button */}
        <Link
          to="/"
          className="absolute top-24 left-6 z-20 flex items-center space-x-2 text-white hover:text-gold-400 transition-colors duration-300"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-light">Back to Properties</span>
        </Link>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 z-20 p-8 md:p-12">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <Star className="w-5 h-5 text-gold-400 fill-gold-400" />
                <span className="text-white font-semibold">{property.rating}</span>
                <span className="text-gray-300">({property.reviews} reviews)</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-serif text-white mb-4">
                {property.name}
              </h1>
              
              <p className="text-xl text-gold-400 font-light italic mb-4">
                "{property.tagline}"
              </p>
              
              <div className="flex items-center space-x-2 text-gray-300">
                <MapPin className="w-5 h-5 text-gold-400" />
                <span>{property.address}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Action Buttons - Sticky */}
      <div className="sticky top-16 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              {propertyData.highlights.map((highlight, index) => (
                <span key={index} className="hidden md:inline-flex items-center text-sm text-gray-600">
                  <div className="w-1.5 h-1.5 bg-gold-400 rotate-45 mr-2" />
                  {highlight}
                </span>
              ))}
            </div>
            <div className="flex items-center space-x-3">
              <a
                href={`tel:${property.phone}`}
                className="flex items-center space-x-2 px-4 py-2 bg-navy-900 text-white hover:bg-navy-800 transition-colors duration-300 rounded"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">Call Now</span>
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors duration-300 rounded"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="hidden sm:inline">WhatsApp</span>
              </a>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowBookingForm(true)}
                className="flex items-center space-x-2 px-6 py-2 bg-gold-400 text-navy-900 hover:bg-gold-500 transition-colors duration-300 rounded font-semibold"
              >
                <Calendar className="w-4 h-4" />
                <span>Book Now</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal direction="up">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-serif text-navy-900 mb-6">
                About {property.name}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                {property.description}
              </p>
              
              {/* Amenities Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
                {property.amenities.map((amenity, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ y: -5 }}
                    className="bg-gray-50 p-4 rounded-lg text-center hover:bg-gold-50 transition-colors duration-300"
                  >
                    <span className="text-gray-700">{amenity}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Room Categories */}
      <section id="rooms-section" className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <ScrollReveal direction="up">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
                <Bed className="mx-4 w-6 h-6 text-gold-400" />
                <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-navy-900 mb-4">
                Room Categories
              </h2>
              <p className="text-gray-600">Choose from our carefully designed accommodations</p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {propertyData.rooms.map((room, index) => (
              <ScrollReveal key={index} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group"
                >
                  {/* Room Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={room.image}
                      alt={room.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-2xl font-serif text-white">{room.name}</div>
                    </div>
                  </div>

                  {/* Room Details */}
                  <div className="p-6">
                    <p className="text-gray-600 text-sm mb-4">{room.description}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {room.occupancy}
                      </span>
                      <span className="flex items-center">
                        <Bath className="w-4 h-4 mr-1" />
                        {room.size}
                      </span>
                    </div>

                    {/* Amenities */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {room.amenities.slice(0, 4).map((amenity, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {amenity}
                        </span>
                      ))}
                      {room.amenities.length > 4 && (
                        <span className="text-xs bg-gold-100 text-gold-600 px-2 py-1 rounded">
                          +{room.amenities.length - 4} more
                        </span>
                      )}
                    </div>

                    <a
                      href={`https://wa.me/${propertyData.whatsapp.replace(/\+/g, '')}?text=Hi, I'm interested in the ${room.name} at ${property.name}. Please share the rates and availability.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-3 bg-green-600 text-white hover:bg-green-700 transition-colors duration-300 rounded font-semibold text-center"
                    >
                      <span className="flex items-center justify-center space-x-2">
                        <MessageCircle className="w-5 h-5" />
                        <span>WhatsApp for Rates</span>
                      </span>
                    </a>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Location Map */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal direction="up">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif text-navy-900 mb-4">
                Location
              </h2>
              <p className="text-gray-600">{property.address}</p>
            </div>

            <div className="max-w-5xl mx-auto rounded-xl overflow-hidden shadow-xl">
              <iframe
                src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3666.4744791877973!2d77.39874!3d23.188899!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397c43b0dcf6ad33%3A0x6c8e93be0c8c9d5f!2s${encodeURIComponent(property.name)}!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin`}
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${property.name} Location`}
              />
            </div>

            {/* Contact CTA */}
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <a
                href={`tel:${property.phone}`}
                className="flex items-center space-x-2 px-8 py-4 bg-navy-900 text-white hover:bg-navy-800 transition-colors duration-300 rounded-lg font-semibold"
              >
                <Phone className="w-5 h-5" />
                <span>Call {property.phone}</span>
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-8 py-4 bg-green-600 text-white hover:bg-green-700 transition-colors duration-300 rounded-lg font-semibold"
              >
                <MessageCircle className="w-5 h-5" />
                <span>Chat on WhatsApp</span>
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default PropertyDetail;
